import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Route, Routes } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import Loading from '../../../components/global/loading/Loading';
import OrderCard from '../../../components/global/orderCard/OrderCard';
import { useOrder } from '../../../context/OrderContext';
import translateDevlieryMethod from '../../../helperFunctions/translateDeliveryMethod';
import translateOrderStatus from '../../../helperFunctions/translateOrderStatus';
import translatePaymentStatus from '../../../helperFunctions/translatePaymentStatus';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  DeliveryMethod,
  Order as OrderType,
  OrderStatus,
  PaymentStatus,
} from '../../../models/Order';
import Order from '../../odrer/Order';

function Orders() {
  const { searchOrders } = useOrder();

  const { control } = useForm();
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | 'all'>(
    'all',
  );
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod | 'all'>(
    'all',
  );
  const [orderStatus, setOrderStatus] = useState<OrderStatus | 'all'>('all');
  const [limit, setLimit] = useState(10);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedQuery = useDebounce(query, 500);

  const getOrdersData = useCallback(
    async (
      page: number,
      query: string,
      paymentStatus: PaymentStatus | 'all',
      deliveryMethod: DeliveryMethod | 'all',
      orderStatus: OrderStatus | 'all',
      limit: number,
    ) => {
      setIsLoading(true);
      try {
        const { orders, totalPages, totalOrders } = await searchOrders(
          page,
          limit,
          query,
          paymentStatus === 'all' ? undefined : paymentStatus || undefined,
          deliveryMethod === 'all' ? undefined : deliveryMethod || undefined,
          orderStatus === 'all' ? undefined : orderStatus || undefined,
        );
        setOrders(orders);
        setCurrentPage(page);
        setTotalPages(totalPages);
        setTotalOrders(totalOrders);
      } finally {
        setIsLoading(false);
      }
    },
    [searchOrders],
  );

  const resetFilters = () => {
    setPaymentStatus('all');
    setDeliveryMethod('all');
    setOrderStatus('all');
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (paymentStatus !== 'all') count++;
    if (deliveryMethod !== 'all') count++;
    if (orderStatus !== 'all') count++;
    return count;
  };

  useEffect(() => {
    getOrdersData(
      currentPage,
      debouncedQuery,
      paymentStatus,
      deliveryMethod,
      orderStatus,
      limit,
    );
  }, [
    debouncedQuery,
    paymentStatus,
    deliveryMethod,
    orderStatus,
    limit,
    currentPage,
    getOrdersData,
  ]);

  return (
    <div className="flex flex-col items-stretch">
      <Routes>
        <Route
          path="/"
          element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <ShoppingBagIcon className="text-primary" />
                    Lista zamówień
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Zarządzaj zamówieniami, filtruj i śledź ich statusy
                  </p>
                </motion.div>

                <motion.div
                  className="flex items-center gap-3"
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <FormControl variant="outlined" size="small">
                    <InputLabel>Limit</InputLabel>
                    <Select
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                      label="Limit"
                      className="w-24"
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <button
                    onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    className={`
                      flex items-center gap-1 py-2 px-3 rounded-md text-sm font-medium
                      ${isFiltersVisible ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                      transition-colors duration-200
                    `}
                  >
                    <FilterListIcon fontSize="small" />
                    <span>Filtry</span>
                    {getActiveFiltersCount() > 0 && (
                      <span className="ml-1 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                        {getActiveFiltersCount()}
                      </span>
                    )}
                  </button>
                </motion.div>
              </div>

              {/* Search */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                <Controller
                  name="search"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <TextField
                        {...field}
                        placeholder="Szukaj zamówienia (email, numer zamówienia, imię/nazwisko)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                          startAdornment: <div className="w-6"></div>,
                          endAdornment: query ? (
                            <ClearIcon
                              className="cursor-pointer text-gray-400 hover:text-gray-600"
                              fontSize="small"
                              onClick={() => setQuery('')}
                            />
                          ) : null,
                        }}
                      />
                    </div>
                  )}
                />
              </motion.div>

              {/* Filters */}
              <AnimatePresence>
                {isFiltersVisible && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-medium text-gray-700">
                          Filtry zaawansowane
                        </h3>
                        <button
                          onClick={resetFilters}
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          <ClearIcon fontSize="small" />
                          Resetuj filtry
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Payment Status filter */}
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Status płatności</InputLabel>
                          <Select
                            value={paymentStatus}
                            onChange={(e) =>
                              setPaymentStatus(e.target.value as PaymentStatus)
                            }
                            label="Status płatności"
                          >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {Object.values(PaymentStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {translatePaymentStatus(status)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Delivery Method filter */}
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Metoda dostawy</InputLabel>
                          <Select
                            value={deliveryMethod}
                            onChange={(e) =>
                              setDeliveryMethod(
                                e.target.value as DeliveryMethod,
                              )
                            }
                            label="Metoda dostawy"
                          >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {Object.values(DeliveryMethod).map((method) => (
                              <MenuItem key={method} value={method}>
                                {translateDevlieryMethod(method)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Order Status filter */}
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Status zamówienia</InputLabel>
                          <Select
                            value={orderStatus}
                            onChange={(e) =>
                              setOrderStatus(e.target.value as OrderStatus)
                            }
                            label="Status zamówienia"
                          >
                            <MenuItem value="all">Wszystkie</MenuItem>
                            {Object.values(OrderStatus).map((status) => (
                              <MenuItem key={status} value={status}>
                                {translateOrderStatus(status)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {getActiveFiltersCount() > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="text-xs text-gray-500">
                            Aktywne filtry:
                          </span>

                          {paymentStatus !== 'all' && (
                            <span className="bg-primary/10 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                              Status płatności:{' '}
                              {translatePaymentStatus(
                                paymentStatus as PaymentStatus,
                              )}
                              <ClearIcon
                                fontSize="small"
                                className="ml-1 w-3 h-3 cursor-pointer"
                                onClick={() => setPaymentStatus('all')}
                              />
                            </span>
                          )}

                          {deliveryMethod !== 'all' && (
                            <span className="bg-primary/10 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                              Dostawa:{' '}
                              {translateDevlieryMethod(
                                deliveryMethod as DeliveryMethod,
                              )}
                              <ClearIcon
                                fontSize="small"
                                className="ml-1 w-3 h-3 cursor-pointer"
                                onClick={() => setDeliveryMethod('all')}
                              />
                            </span>
                          )}

                          {orderStatus !== 'all' && (
                            <span className="bg-primary/10 text-primary text-xs py-1 px-2 rounded-full flex items-center">
                              Status:{' '}
                              {translateOrderStatus(orderStatus as OrderStatus)}
                              <ClearIcon
                                fontSize="small"
                                className="ml-1 w-3 h-3 cursor-pointer"
                                onClick={() => setOrderStatus('all')}
                              />
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Content */}
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="loading"
                    className="flex justify-center py-12"
                  >
                    <Loading />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="content"
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Status bar */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      <div>
                        Wyświetlono{' '}
                        <span className="font-semibold">{orders.length}</span> z{' '}
                        <span className="font-semibold">{totalOrders}</span>{' '}
                        zamówień
                      </div>
                      <div className="flex items-center gap-2">
                        <span>
                          Strona {currentPage} z {totalPages}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() =>
                              currentPage > 1 &&
                              getOrdersData(
                                currentPage - 1,
                                query,
                                paymentStatus,
                                deliveryMethod,
                                orderStatus,
                                limit,
                              )
                            }
                            disabled={currentPage <= 1}
                            className={`p-1 rounded ${currentPage <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200'}`}
                          >
                            <ArrowBackIosNewIcon
                              style={{ fontSize: '0.9rem' }}
                            />
                          </button>
                          <button
                            onClick={() =>
                              currentPage < totalPages &&
                              getOrdersData(
                                currentPage + 1,
                                query,
                                paymentStatus,
                                deliveryMethod,
                                orderStatus,
                                limit,
                              )
                            }
                            disabled={currentPage >= totalPages}
                            className={`p-1 rounded ${currentPage >= totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200'}`}
                          >
                            <ArrowForwardIosIcon
                              style={{ fontSize: '0.9rem' }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Order cards */}
                    {orders.length > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        <AnimatePresence>
                          {orders.map((order, index) => (
                            <motion.div
                              key={order.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.3,
                                delay: index * 0.05,
                              }}
                            >
                              <OrderCard order={order} />
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <ShoppingBagIcon
                          style={{ fontSize: '3rem' }}
                          className="text-gray-300 mb-2"
                        />
                        <h3 className="text-lg font-medium text-gray-700">
                          Brak zamówień
                        </h3>
                        <p className="text-gray-500 mt-1">
                          Nie znaleziono żadnych zamówień spełniających podane
                          kryteria
                        </p>
                      </div>
                    )}

                    {/* Paginacja dla większych liczb stron */}
                    {totalPages > 1 && (
                      <div className="flex justify-center py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              getOrdersData(
                                1,
                                query,
                                paymentStatus,
                                deliveryMethod,
                                orderStatus,
                                limit,
                              )
                            }
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded text-sm ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          >
                            Pierwsza
                          </button>

                          {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                              pageNum = idx + 1;
                            } else if (currentPage <= 3) {
                              pageNum = idx + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + idx;
                            } else {
                              pageNum = currentPage - 2 + idx;
                            }

                            return (
                              <button
                                key={idx}
                                onClick={() =>
                                  getOrdersData(
                                    pageNum,
                                    query,
                                    paymentStatus,
                                    deliveryMethod,
                                    orderStatus,
                                    limit,
                                  )
                                }
                                className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                                  ${currentPage === pageNum ? 'bg-primary text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          <button
                            onClick={() =>
                              getOrdersData(
                                totalPages,
                                query,
                                paymentStatus,
                                deliveryMethod,
                                orderStatus,
                                limit,
                              )
                            }
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded text-sm ${currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          >
                            Ostatnia
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          }
        />
        <Route path=":id/*" element={<Order />} />
      </Routes>
    </div>
  );
}

export default Orders;
